import { AppStateType } from '../../types';
import { createSelector } from 'reselect';
import { DataNames } from './userSettingsSlice';
import { FavoriteFdlType } from './types';
import { FAVORITE_FDL_STAC_PREFIX } from './decodeFavoriteFdlType';
import {
  isFieldDataLayerType,
  isStacFieldDataLayerType,
} from './fieldDataLayerTypeGuards';

export const userSettingsSelector = (state: AppStateType) => {
  return state.users.settings;
};

export const userSettingsStatusSelector = createSelector(
  userSettingsSelector,
  (settings) => settings.status,
);

export const userSettingsLastFarmSelectedUuidSelector = createSelector(
  userSettingsSelector,
  (settings) => {
    return settings.data?.find(
      (setting) => setting.name === DataNames.LAST_FARM_SELECTED,
    )?.value;
  },
);
export const userSettingsTermsAndConditionsUuidSelector = createSelector(
  userSettingsSelector,
  (settings) => {
    return settings.data?.find((setting) => setting.name === 'IS_T&C_ACCEPTED')
      ?.uuid;
  },
);
export const userSettingsBASFConsentsUuidSelector = createSelector(
  userSettingsSelector,
  (settings) => {
    return settings.data?.find((setting) => setting.name === 'BASF_CONSENT')
      ?.uuid;
  },
);
export const userSettingsTermAndCondtionsSelector = createSelector(
  userSettingsSelector,
  (settings) => {
    return settings.data?.find((setting) => setting.name === 'IS_T&C_ACCEPTED')
      ?.value;
  },
);
export const userSettingsIsShopMessageShownSelector = createSelector(
  userSettingsSelector,
  (settings) => {
    return settings.data?.find(
      (setting) => setting.name === DataNames.IS_SHOP_2021_MESSAGE_SHOWN,
    );
  },
);
export const userSettingsBASFConsentSelector = createSelector(
  userSettingsSelector,
  (settings) => {
    return settings.data?.find(
      (setting) => setting.name === DataNames.BASF_CONSENT,
    );
  },
);

export const userSettingsYaraConsentSelector = createSelector(
  userSettingsSelector,
  (settings) => {
    return settings.data?.find(
      (setting) => setting.name === DataNames.YARA_CONSENT,
    );
  },
);

export const userSettingsKeepDataConsentSelector = createSelector(
  userSettingsSelector,
  (settings) => {
    return settings.data?.find(
      (setting) => setting.name === DataNames.KEEP_DATA_CONSENT,
    );
  },
);

export const userSettingsZennohConsentSelector = createSelector(
  userSettingsSelector,
  (settings) => {
    return settings.data?.find(
      (setting) => setting.name === DataNames.ZENNOH_CONSENT,
    );
  },
);

export const userSettingsLastDistributorSelector = createSelector(
  userSettingsSelector,
  (settings) => {
    return settings.data?.find(
      (setting) => setting.name === DataNames.LAST_SELECTED_DISTRIBUTOR,
    );
  },
);

export const userSettingsLastPrefectureSelector = createSelector(
  userSettingsSelector,
  (settings) => {
    return settings.data?.find(
      (setting) => setting.name === DataNames.LAST_SELECTED_PREFECTURE,
    );
  },
);

export const userSettingsNotificationsLastReadAtSelector = createSelector(
  userSettingsSelector,
  (settings) => {
    return settings.data?.find(
      (setting) => setting.name === DataNames.NOTIFICATIONS_LAST_READ_AT,
    );
  },
);

export const userSettingsAgDashboardConfigurationSelector = createSelector(
  userSettingsSelector,
  (settings) => {
    return settings.data?.find(
      (setting) => setting.name === DataNames.AG_DASHBOARD_CONFIGURATION,
    );
  },
);

export const userSettingsHFDashboardConfigurationSelector = createSelector(
  userSettingsSelector,
  (settings) => {
    return settings.data?.find(
      (setting) => setting.name === DataNames.HF_DASHBOARD_CONFIGURATION,
    );
  },
);

export const userSettingsMFEVConfigurationSelector = createSelector(
  userSettingsSelector,
  (settings) => {
    return settings.data?.find(
      (setting) => setting.name === DataNames.MFEV_CONFIGURATION,
    );
  },
);

export const userSettingsLastTerminalSelected = createSelector(
  userSettingsSelector,
  (settings) => {
    return settings.data?.find(
      (setting) =>
        setting.name === DataNames.LAST_TERMINAL_SELECTED &&
        setting.value !== 'UNKNOWN',
    )?.value;
  },
);

export const userSettingsLastXConnectSelected = createSelector(
  userSettingsSelector,
  (settings) => {
    return settings.data?.find(
      (setting) =>
        setting.name === DataNames.LAST_XCONNECT_SELECTED &&
        setting.value !== 'UNKNOWN',
    )?.value;
  },
);

export const userSettingsLastFileFormatSelected = createSelector(
  userSettingsSelector,
  (settings) => {
    return settings.data?.find(
      (setting) =>
        setting.name === DataNames.LAST_SELECTED_EXPORT_FILE_FORMAT &&
        setting.value !== 'UNKNOWN',
    )?.value;
  },
);

export const userSettingsStatusPageMapTypeSelector = createSelector(
  userSettingsSelector,
  (settings) => {
    return settings.data?.find(
      (setting) => setting.name === DataNames.STATUS_PAGE_SELECTED_MAP_TYPE,
    )?.value;
  },
);

export const userSettingsAnalyticsOverviewFavorites = createSelector(
  userSettingsSelector,
  (settings): FavoriteFdlType[] | 'UNKNOWN' => {
    const settingValue = settings.data?.find(
      (setting) =>
        setting.name === DataNames.ANALYTICS_OVERVIEW_FAVORITES &&
        setting.value !== 'UNKNOWN',
    )?.value;

    if (!settingValue) {
      return 'UNKNOWN';
    }

    try {
      return JSON.parse(settingValue).filter(
        (str) =>
          isFieldDataLayerType(str) ||
          isStacFieldDataLayerType(str.slice(FAVORITE_FDL_STAC_PREFIX.length)),
      );
    } catch {
      console.error(
        `userSettingsAnalyticsOverviewFavorites: Error while parsing setting ${DataNames.ANALYTICS_OVERVIEW_FAVORITES}`,
      );
      return 'UNKNOWN';
    }
  },
);
